import { Fragment, useEffect, useRef } from 'react';
import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { MoreVerticalIcon } from 'lucide-react';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import { useVideoPreview } from '@/shared/hooks/useVideoPreview';
import OptimizedImage from '@/shared/ui/OptimizedImage';
import { TruncatedBadge } from '@/shared/ui';
import type { PersonaCardProps } from './PersonaCard.types';
import { useTranslation } from 'react-i18next';

export const PersonaCard = ({
  name,
  title,
  tags = [],
  description,
  photoAssetId,
  mouseOverAssetId,
  onClick,
  actions = [],
  badge,
  badgePlacement = 'image',
  imageFallback,
}: PersonaCardProps) => {
  const { t } = useTranslation('audiences');

  const imageId = photoAssetId || '';
  const { downloadUrl: personaImage } = useGetDownloadUrl(imageId);
  const videoId = mouseOverAssetId || '';
  const { downloadUrl: videoCover } = useGetDownloadUrl(videoId);

  const { videoPreloadRef, showVideo, handleMouseEnter, handleMouseLeave } = useVideoPreview({
    videoCover,
  });

  // Restart the preview from the beginning on hover and pause it when not hovering,
  // matching the Audience surface behavior.
  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (showVideo) {
      video.currentTime = 0;
      void video.play();
    } else {
      video.pause();
    }
  }, [showVideo]);

  const hasActions = actions.length > 0;
  const heading = name || title || t('persona.personaCard.unnamedPersona');

  return (
    <Card
      className={clsx(
        'group relative flex flex-col gap-0 overflow-hidden shadow-none! rounded-lg border border-border-light p-0 dark:bg-transparent hover:shadow-none! transition-[border-color,box-shadow] duration-fast ease-out',
        onClick && 'cursor-pointer hover:border-border-strong',
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hidden video element for preloading */}
      {videoCover && (
        <video
          ref={videoPreloadRef}
          src={videoCover}
          preload="auto"
          muted
          playsInline
          style={{ display: 'none' }}
          aria-hidden="true"
        >
          <track kind="captions" />
        </video>
      )}
      {/* Persona image */}
      <div className="relative h-48 w-full overflow-hidden">
        {!personaImage && imageFallback && <div className="absolute inset-0">{imageFallback}</div>}

        {personaImage && (
          <OptimizedImage
            src={personaImage}
            alt={t('persona.personaCard.profileImage', { name: heading })}
            containerClassName="w-full h-full"
            className="h-full w-full scale-105 object-cover object-top"
          />
        )}

        {/* Hover video overlay */}
        {videoCover && (
          <video
            ref={videoRef}
            src={videoCover}
            muted
            loop
            playsInline
            className={`absolute inset-0 h-full w-full scale-105 object-cover object-top transition-opacity duration-base ${
              showVideo ? 'opacity-100' : 'opacity-0'
            }`}
            aria-hidden="true"
          >
            <track kind="captions" />
          </video>
        )}

        {badge && badgePlacement === 'image' && (
          <div className="pointer-events-none absolute bottom-3 left-4">{badge}</div>
        )}

        {/* Visible on card hover or keyboard focus, or while the menu is open. Stacked above the
            card's stretched button so the trigger stays clickable. */}
        {hasActions && (
          <div className="absolute right-2 top-2 z-10 opacity-0 transition-opacity duration-base group-hover:opacity-100 group-focus-within:opacity-100 has-[[data-state=open]]:opacity-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="mini"
                  aria-label={t('persona.personaCard.personaActions')}
                  className="size-8 bg-background/70 p-0 hover:bg-background"
                >
                  <MoreVerticalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {actions.map((action) => {
                  const item = (
                    <DropdownMenuItem disabled={action.disabled} onSelect={action.onSelect}>
                      {action.label}
                    </DropdownMenuItem>
                  );
                  if (!action.disabled || !action.disabledReason) {
                    return <Fragment key={action.label}>{item}</Fragment>;
                  }
                  return (
                    <TooltipProvider key={action.label} delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>{item}</span>
                        </TooltipTrigger>
                        <TooltipContent side="left">{action.disabledReason}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2.5 px-5 py-4.5">
        <h3 className="type-heading-sm text-foreground">
          {onClick ? (
            // The button's ::after covers the whole card, so a click anywhere opens it while
            // keyboard and screen-reader users get one native control named after the persona.
            // Anything interactive below (citations, tag tooltips) is raised above that overlay.
            <button
              type="button"
              onClick={onClick}
              className="text-left outline-none after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:after:focus-ring focus-visible:after:ring-inset"
            >
              {heading}
            </button>
          ) : (
            heading
          )}
        </h3>

        {/* Tags */}
        {(tags.length > 0 || (badge && badgePlacement === 'tags')) && (
          <div className="flex flex-wrap gap-xs empty:hidden [&_[data-state]]:relative [&_[data-state]]:z-10">
            {tags.slice(0, 3).map((tag) => (
              <TruncatedBadge key={tag} tag={tag} variant="secondary" roundness="full" size="sm" />
            ))}
            {badgePlacement === 'tags' && badge}
          </div>
        )}

        {/* Description — muted, clamped to 4 lines max */}
        {description && (
          <div className="line-clamp-4 overflow-hidden type-body-md text-foreground-subtle [&_button]:relative [&_button]:z-10">
            {description}
          </div>
        )}
      </div>
    </Card>
  );
};
